<script lang="ts" setup>
  import { type ComponentPublicInstance, computed, onMounted, useTemplateRef, watch } from 'vue';
  import type { Player } from '../../../../shared/api/player.ts';
  import type { SongSubmissionScore } from '../../../../shared/api/round.ts';
  import RoundResultHeader, { type RoundResultVariant } from './RoundResultHeader.vue';
  import RoundResultRow from './RoundResultRow.vue';

  const songsShownForLastSeconds = 10;

  const props = defineProps<{
    secondsLeft?: string;
    chooserPlayer?: Player;
    player: Player;
    songSubmissionScores: SongSubmissionScore[];
  }>();

  const variant = computed<RoundResultVariant>(() =>
    !props.secondsLeft || Number(props.secondsLeft) > songsShownForLastSeconds ? 'score' : 'song',
  );

  const resultsContentRef = useTemplateRef<HTMLElement>('results-content');
  const rowRefs = useTemplateRef<ComponentPublicInstance[]>('row-refs');

  const scrollToPlayer = () => {
    const container = resultsContentRef.value;
    if (!container || !rowRefs.value) return;

    const playerIndex = props.songSubmissionScores.findIndex(
      (score) => score.playerStanding.player.id === props.player.id,
    );
    if (playerIndex === -1) {
      container.scrollTop = 0;
      return;
    }

    const row = rowRefs.value[playerIndex].$el as HTMLElement;
    const rowOffset = row.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTop = rowOffset - (container.clientHeight - row.offsetHeight) / 2;
  };

  onMounted(scrollToPlayer);
  watch(() => [props.player, props.songSubmissionScores], scrollToPlayer, { flush: 'post' });
</script>

<template>
  <div v-if="songSubmissionScores.length > 0" class="round-result-list">
    <div class="results-header-wrapper">
      <RoundResultHeader :variant="variant" />
    </div>
    <div ref="results-content" :data-variant="variant" class="results-content" tabindex="0">
      <RoundResultRow
        v-for="songSubmissionScore in songSubmissionScores"
        :key="songSubmissionScore.playerStanding.player.id"
        ref="row-refs"
        :chooser-player="chooserPlayer"
        :player="player"
        :song-submission-score="songSubmissionScore"
        :variant="variant"
      />
    </div>
  </div>
</template>

<style scoped>
  .round-result-list {
    --placement-width: 1.5rem;
    --row-inset: 0.5rem;
    --score-width: 3.5rem;
    --scrollbar-gutter: 0.25rem;

    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.25rem;
    max-width: var(--control-max-width);
    min-height: 0;
    width: 100%;
  }

  .results-header-wrapper {
    padding-right: var(--scrollbar-gutter);
  }

  .results-content {
    align-content: start;
    display: grid;
    flex: 1 1 auto;
    gap: 0.25rem 0.5rem;
    min-height: 0;
    overflow-y: auto;
    padding-right: var(--scrollbar-gutter);
    scrollbar-color: var(--color-primary) var(--color-scrollbar-track);
    scrollbar-width: thin;
  }

  .results-content[data-variant='score'] {
    grid-template-columns:
      calc(var(--placement-width) + var(--row-inset)) minmax(0, 1fr) var(--score-width)
      calc(var(--score-width) + var(--row-inset));
  }

  .results-content[data-variant='song'] {
    grid-template-columns: calc(var(--placement-width) + var(--row-inset)) max-content minmax(0, 1fr);
  }

  .results-content::-webkit-scrollbar {
    width: 0.4rem;
  }

  .results-content::-webkit-scrollbar-track {
    background: var(--color-scrollbar-track);
    border-radius: var(--radius-sm);
  }

  .results-content::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: var(--radius-sm);
  }

  .results-content::-webkit-scrollbar-thumb:hover {
    background: color-mix(in srgb, var(--color-primary), var(--color-tint) 20%);
  }
</style>
